import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface Randevu {
  id: string;
  musteri_id: string;
  musteri_ad: string;
  musteri_soyad: string;
  musteri_telefon: string;
  musteri_adres: string;
  musteri_ilce: string;
  servis_tarihi: string;
  servis_saati: string;
  islem_turu: string;
  servis_notu: string;
  personel_id: string | null;
  personel_ad?: string;
  personel_soyad?: string;
  renk: string;
}

const islemRenkMap: Record<string, string> = {
  'Filtre Bakımı – Dörtlü': 'sky',
  'Filtre Bakımı – Beşli': 'indigo',
  'Genel Bakım': 'emerald',
  'Yeni Montaj': 'violet',
  'Bina Girişi': 'teal',
  'Bina Girişi Bakımı': 'cyan',
  'Taşıma': 'orange',
  'Diğer': 'gray',
};

function getRenk(islem: string): string {
  for (const [key, val] of Object.entries(islemRenkMap)) {
    if (islem.includes(key) || key.includes(islem)) return val;
  }
  return 'sky';
}

export function useRandevular() {
  const [randevular, setRandevular] = useState<Randevu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRandevular = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('sadem_servis_kayitlari')
        .select('*, sadem_musteriler!inner(ad, soyad, telefon, acik_adres, ilce), sadem_personeller(ad, soyad)')
        .eq('aktif', true)
        .eq('servis_durumu', 'planlandi')
        .order('servis_tarihi', { ascending: true })
        .order('servis_saati', { ascending: true });

      if (err) throw err;

      const mapped: Randevu[] = (data || []).map((r: any) => ({
        id: r.id,
        musteri_id: r.musteri_id,
        musteri_ad: r.sadem_musteriler?.ad || '',
        musteri_soyad: r.sadem_musteriler?.soyad || '',
        musteri_telefon: r.sadem_musteriler?.telefon || '',
        musteri_adres: r.sadem_musteriler?.acik_adres || '',
        musteri_ilce: r.sadem_musteriler?.ilce || '',
        servis_tarihi: r.servis_tarihi,
        servis_saati: r.servis_saati,
        islem_turu: r.islem_turu,
        servis_notu: r.servis_notu || '',
        personel_id: r.personel_id,
        personel_ad: r.sadem_personeller?.ad,
        personel_soyad: r.sadem_personeller?.soyad,
        renk: getRenk(r.islem_turu),
      }));

      setRandevular(mapped);
    } catch (err: any) {
      setError(err.message || 'Randevular yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRandevular();
  }, [fetchRandevular]);

  return { randevular, loading, error, refetch: fetchRandevular };
}

export async function createRandevu(data: {
  musteri_id: string;
  servis_tarihi: string;
  servis_saati: string;
  islem_turu: string;
  servis_notu?: string;
}) {
  const { error } = await supabase.from('sadem_servis_kayitlari').insert({
    musteri_id: data.musteri_id,
    servis_tarihi: data.servis_tarihi,
    servis_saati: data.servis_saati,
    islem_turu: data.islem_turu,
    servis_durumu: 'planlandi',
    servis_notu: data.servis_notu || '',
    aktif: true,
  });
  if (error) throw error;
}

export async function updateRandevu(id: string, data: {
  servis_tarihi?: string;
  servis_saati?: string;
  islem_turu?: string;
  servis_notu?: string;
}) {
  const { error } = await supabase.from('sadem_servis_kayitlari').update(data).eq('id', id);
  if (error) throw error;
}

export async function deleteRandevu(id: string) {
  const { error } = await supabase.from('sadem_servis_kayitlari').update({ aktif: false }).eq('id', id);
  if (error) throw error;
}